import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  makeStyles,
  withStyles,
  Divider,
} from "@material-ui/core";
import axios from "axios";
import { Check } from "@material-ui/icons";
import { useApiBaseUrl } from "../../../CommonMethods/CommonMethods";
import { appUrl } from "../../../../../../../utils/config";

const useStyles = makeStyles((theme) => ({
  iosSwitch: {
    "& > *": {
      height: "22px !important",
    },
    "& .MuiSwitch-switchBase .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "& .MuiSwitch-switchBase": {
      left: "2px",
      top: "-1px",
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      left: "auto",
      right: "18px",
    },
    "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-track": {
      backgroundColor: "#1890ff !important",
    },
  },
  checkedSwitch: {
    position: "absolute",
    width: "16px !important",
    left: "12px",
    top: "-3px",
    color: "white",
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  actionButtons: {
    display: "flex",
    flexDirection: "column",
    marginTop: 4,
  },
  mandatoryDropdown: {
    width: "100%", // This will make it take full available width
    "& .MuiSelect-root": {
      fontSize: "14px",
      padding: "8px 12px",
      textAlign: "left",
    },
    "& .MuiSelect-icon": {
      right: "8px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#c0c0c0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#a0a0a0",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1890ff",
    },
    "& .Mui-disabled": {
      color: "rgba(0, 0, 0, 0.87) !important", // text color same as enabled
      "-webkit-text-fill-color": "rgba(0, 0, 0, 0.87) !important", // safari fix
    },
    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: "#c0c0c0 !important", // keep border visible
    },
  },
}));

const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 44,
    height: 22,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "& .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#1890ff !important",
        opacity: 1,
        border: "none",
      },
      "& .MuiSwitch-track": {
        backgroundColor: "#1890ff !important",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 18,
    height: 18,
  },
  track: {
    borderRadius: 26 / 2,
    backgroundColor: "#c6c6c6",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
}))(({ classes, ...props }) => (
  <Switch
    focusVisibleClassName={classes.focusVisible}
    disableRipple
    disabled={props.disabled}
    classes={{
      root: classes.root,
      switchBase: classes.switchBase,
      thumb: classes.thumb,
      track: classes.track,
      checked: classes.checked,
    }}
    {...props}
  />
));

const ProductsTypesSection = forwardRef(
  (
    {
      productTypesData: initialProductsTypesData = [],
      isProductsLoading: initialProductsTypesLoading = false,
      editMode: initialEditMode = false,
      vendorMosymphonyID,
      brandOwnerMosymphonyID,
      onChange,
    },
    ref
  ) => {
    const classes = useStyles();
    const [productTypesData, setProductTypesData] = useState([
      ...initialProductsTypesData,
    ]);
    const [isProductsLoading, setIsProdLoading] = useState(
      initialProductsTypesLoading
    );
    const [editMode, setEditMode] = useState(initialEditMode);
    const [isModified, setIsModified] = useState(false);
    const [originalProductsTypesData, setOriginalProductsTypesData] = useState([
      ...initialProductsTypesData,
    ]);
    const API_BASE_URL = useApiBaseUrl(appUrl);
    const token = localStorage.getItem("authToken");
    // Update state when props change
    useEffect(() => {
      setProductTypesData([...initialProductsTypesData]);
      setOriginalProductsTypesData([...initialProductsTypesData]);
      setIsProdLoading(initialProductsTypesLoading);
      setEditMode(initialEditMode);
      setIsModified(false);
    }, [
      initialProductsTypesData,
      initialProductsTypesLoading,
      initialEditMode,
    ]);

    // Check if data has been modified
    useEffect(() => {
      const hasChanged =
        JSON.stringify(productTypesData) !==
        JSON.stringify(originalProductsTypesData);
      if (isModified !== hasChanged) {
        setIsModified(hasChanged);
      }
    }, [productTypesData, originalProductsTypesData, isModified]);

    // Fetch document types when entering edit mode
    useEffect(() => {
      let isMounted = true;
      const fetchDocumentTypes = async () => {
        // if (editMode && !isProductsLoading) {
        if (!isProductsLoading) {
          setIsProdLoading(true);
          try {
            const productConfig = true;
            const response = await axios.get(
              `${API_BASE_URL}/fetchProductTypes/${productConfig}`,
            );
            if (response.data.data.productTypeList && isMounted) {
              const fetchedData = response.data.data.productTypeList.map(
                (doc) => ({
                  productTypeID: doc.productTypeID,
                  productTypeName: doc.productTypeName,
                  activated: true,
                  consumptionDataReqdForIBDGRN: [1, 2].includes(
                    doc.productTypeID,
                  )
                    ? false
                    : doc.productType === "Default"
                      ? true
                      : false,
                      // : "NA",
                  uploadConsumptionData:
                    ![1, 2].includes(doc.productTypeID) === true &&
                    doc.productType === "Default"
                      ? true
                      : false,
                      // : "NA",
                      consumptionSameAsAllocation: false,
                  manuallyAddConsumptionData: false,
                  expiryDateProvided: true,
                  // consumptionSameAsAllocation: "NA",
                  // manuallyAddConsumptionData: "NA",
                }),
              );

              // Merge fetched data with existing data by productTypeID while preserving original order
              const mergedData = productTypesData.map((existingDoc) => {
                const fetchedDoc = fetchedData.find(
                  (fetched) =>
                    fetched.productTypeID === existingDoc.productTypeID,
                );

                if (fetchedDoc) {
                  // Update with fresh data but preserve user modifications and original order
                  return {
                    ...fetchedDoc,
                    productTypeName: fetchedDoc.productTypeName,
                    activated: existingDoc.activated,
                    consumptionDataReqdForIBDGRN:
                      typeof existingDoc.consumptionDataReqdForIBDGRN ===
                      "boolean"
                        ? existingDoc.consumptionDataReqdForIBDGRN
                        : false,
                        // : "NA",
                    uploadConsumptionData:
                      typeof existingDoc.uploadConsumptionData === "boolean"
                        ? existingDoc.uploadConsumptionData
                        : false,
                        // : "NA",
                    consumptionSameAsAllocation:
                      typeof existingDoc.consumptionSameAsAllocation ===
                      "boolean"
                        ? existingDoc.consumptionSameAsAllocation
                        : false,
                        // : "NA",
                    manuallyAddConsumptionData:
                      typeof existingDoc.manuallyAddConsumptionData ===
                      "boolean"
                        ? existingDoc.manuallyAddConsumptionData
                        : false,
                        // : "NA",
                    expiryDateProvided:
                      typeof existingDoc.expiryDateProvided === "boolean"
                        ? existingDoc.expiryDateProvided
                        : true,
                  };
                }

                // If document not found in fetched data, keep the existing one
                return existingDoc;
              });

              // originalMergedData preserves raw server values (including null) for
              // accurate change detection — prevents null → true conversion from
              // masking un-saved expiryDateProvided values
              const originalMergedData = mergedData.map((mergedDoc) => {
                const existingDoc = productTypesData.find(
                  (doc) => doc.productTypeID === mergedDoc.productTypeID,
                );
                if (existingDoc) {
                  return {
                    ...mergedDoc,
                    expiryDateProvided: existingDoc.expiryDateProvided,
                  };
                }
                return mergedDoc;
              });

              if (!editMode) {
                setProductTypesData(mergedData);
                setOriginalProductsTypesData(originalMergedData);
              } else {
                // Add any new documents from fetched data that weren't in existing data
                const existingIds = productTypesData.map(
                  (doc) => doc.productTypeID,
                );
                const newProducts = fetchedData.filter(
                  (fetchedDoc) =>
                    !existingIds.includes(fetchedDoc.productTypeID),
                );

                const finalMergedData = [...mergedData, ...newProducts];

                setProductTypesData(finalMergedData);
                setOriginalProductsTypesData(originalMergedData);
              }
            }
          } catch (error) {
            console.error("Error fetching document types:", error);
          } finally {
            if (isMounted) setIsProdLoading(false);
          }
        }
      };

      fetchDocumentTypes();
      return () => {
        isMounted = false;
      };
    }, [editMode, API_BASE_URL, token]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      getFormData: () => {
        return productTypesData;
      },
      getPayload: () => {
        // Only include documents that have been modified
        const modifiedDocuments = productTypesData.filter((doc) => {
          const originalDoc = originalProductsTypesData.find(
            (orig) => orig.productTypeID === doc.productTypeID,
          );

          // Check if product is modified (activated changed or consumptionDataReqdForIBDGRN changed)
          return (
            !originalDoc ||
            originalDoc.activated !== doc.activated ||
            originalDoc.consumptionDataReqdForIBDGRN !==
              doc.consumptionDataReqdForIBDGRN ||
            originalDoc.uploadConsumptionData !== doc.uploadConsumptionData ||
            originalDoc.consumptionSameAsAllocation !==
              doc.consumptionSameAsAllocation ||
            originalDoc.manuallyAddConsumptionData !==
              doc.manuallyAddConsumptionData ||
            originalDoc.expiryDateProvided !== doc.expiryDateProvided
          );
        });
        const payload = {
          vendorMosymphonyID,
          brandOwnerMosymphonyID,
          vendorPlantDetails: {
            vendorProductTypeConfigs: modifiedDocuments.map((doc) => ({
              productTypeID: doc.productTypeID,
              // productTypeName:
              //   doc.productTypeName?.trim() || doc.productTypeName,
              activated: doc.activated,
              consumptionDataReqdForIBDGRN:
                typeof doc.consumptionDataReqdForIBDGRN === "boolean"
                  ? doc.consumptionDataReqdForIBDGRN
                  : false,
              uploadConsumptionData:
                typeof doc.uploadConsumptionData === "boolean"
                  ? doc.uploadConsumptionData
                  : false,
              consumptionSameAsAllocation:
                typeof doc.consumptionSameAsAllocation === "boolean"
                  ? doc.consumptionSameAsAllocation
                  : false,
              manuallyAddConsumptionData:
                typeof doc.manuallyAddConsumptionData === "boolean"
                  ? doc.manuallyAddConsumptionData
                  : false,
              expiryDateProvided:
                typeof doc.expiryDateProvided === "boolean"
                  ? doc.expiryDateProvided
                  : true,
            })),
          },
          updateVendorProductTypeConfig: true,
        };

        return payload;
      },
      validateForm: () => true, // Document types don't require validation
      resetForm: () => {
        setProductTypesData([...originalProductsTypesData]);
        setIsModified(false);
      },
    }));

    const handleToggle = (id, checked) => {
      setProductTypesData((prev) =>
        prev.map((doc) =>
          doc.productTypeID === id ? { ...doc, activated: checked } : doc,
        ),
      );

      // Notify parent about change
      if (onChange) {
        onChange();
      }
    };

    const handleMandatoryChange = (id, name, value) => {
      setProductTypesData((prev) =>
        prev.map((doc) =>
          doc.productTypeID === id
            ? name === "consumptionDataReqdForIBDGRN" && value === "No"
              ? {
                  ...doc,
                  consumptionDataReqdForIBDGRN: false,
                  uploadConsumptionData: false,
                  consumptionSameAsAllocation: false,
                }
              : { ...doc, [name]: value === "Yes" }
            : doc,
        ),
      );

      // Notify parent about change
      if (onChange) {
        onChange();
      }
    };

    // Read-only view
    // if (!editMode) {
    //   return (
    //     <>
    //       <div className="cmo-stepper-wrapper plant-details-step">
    //         <div className="stepper-container">
    //           <form autoComplete="off">
    //             {isProductsLoading ? (
    //               <div className={classes.loaderContainer}>
    //                 <CircularProgress />
    //               </div>
    //             ) : (
    //               <TableContainer
    //                 component={Paper}
    //                 style={{ minWidth: 400, marginTop: 16, marginBottom: 16 }}
    //               >
    //                 <Table>
    //                   <TableHead style={{ backgroundColor: "#f9f9fc" }}>
    //                     <TableRow>
    //                       <TableCell style={{ fontSize: 16 }}>
    //                         Selection
    //                       </TableCell>
    //                       <TableCell style={{ fontSize: 16 }}>
    //                         Product Type
    //                       </TableCell>
    //                       <TableCell style={{ fontSize: 16 }}>
    //                         Consumption info req. For IBD/GRN
    //                       </TableCell>
    //                       <TableCell style={{ fontSize: 16 }}>
    //                         Consumption data present
    //                       </TableCell>
    //                       <TableCell style={{ fontSize: 16 }}>
    //                         Consumption details = Allocation details
    //                       </TableCell>
    //                       <TableCell style={{ fontSize: 16 }}>
    //                         Consumption data entered manually
    //                       </TableCell>
    //                     </TableRow>
    //                   </TableHead>
    //                   <TableBody>
    //                     {productTypesData.map((doc) => (
    //                       <TableRow key={doc.productTypeID}>
    //                         <TableCell>
    //                           <span style={{ position: "relative" }}>
    //                             <IOSSwitch
    //                               className={classes.iosSwitch}
    //                               checked={doc.activated === true}
    //                               disabled={true}
    //                               color="primary"
    //                             />
    //                             {doc.activated === true && (
    //                               <Check className={classes.checkedSwitch} />
    //                             )}
    //                           </span>
    //                         </TableCell>
    //                         <TableCell>{doc.productTypeName}</TableCell>
    //                         <TableCell>
    //                           <FormControl
    //                             variant="outlined"
    //                             size="small"
    //                             className={classes.mandatoryDropdown}
    //                           >
    //                             <Select
    //                               value={
    //                                 typeof doc.consumptionDataReqdForIBDGRN === "boolean"
    //                                   ? doc.consumptionDataReqdForIBDGRN
    //                                     ? "Yes"
    //                                     : "No"
    //                                   : "NA"
    //                               }
    //                               disabled={true}
    //                               displayEmpty
    //                             >
    //                               <MenuItem value="NA">NA</MenuItem>
    //                               <MenuItem value="No">No</MenuItem>
    //                               <MenuItem value="Yes">Yes</MenuItem>
    //                             </Select>
    //                           </FormControl>
    //                         </TableCell>
    //                         <TableCell>
    //                           <FormControl
    //                             variant="outlined"
    //                             size="small"
    //                             className={classes.mandatoryDropdown}
    //                           >
    //                             <Select
    //                               value={
    //                                 typeof doc.consumptionDataReqdForIBDGRN === "boolean"
    //                                   ? doc.consumptionDataReqdForIBDGRN
    //                                     ? "Yes"
    //                                     : "No"
    //                                   : "NA"
    //                               }
    //                               disabled={true}
    //                               displayEmpty
    //                             >
    //                               <MenuItem value="NA">NA</MenuItem>
    //                               <MenuItem value="No">No</MenuItem>
    //                               <MenuItem value="Yes">Yes</MenuItem>
    //                             </Select>
    //                           </FormControl>
    //                         </TableCell>

    //                         <TableCell>
    //                           <FormControl
    //                             variant="outlined"
    //                             size="small"
    //                             className={classes.mandatoryDropdown}
    //                           >
    //                             <Select
    //                               value={
    //                                 typeof doc.uploadConsumptionData ===
    //                                 "boolean"
    //                                   ? doc.uploadConsumptionData
    //                                     ? "Yes"
    //                                     : "No"
    //                                   : "NA"
    //                               }
    //                               disabled={true}
    //                               displayEmpty
    //                             >
    //                               <MenuItem value="NA">NA</MenuItem>
    //                               <MenuItem value="No">No</MenuItem>
    //                               <MenuItem value="Yes">Yes</MenuItem>
    //                             </Select>
    //                           </FormControl>
    //                         </TableCell>
    //                         <TableCell>
    //                           <FormControl
    //                             variant="outlined"
    //                             size="small"
    //                             className={classes.mandatoryDropdown}
    //                           >
    //                             <Select
    //                               value={
    //                                 typeof doc.manuallyAddConsumptionData === "boolean"
    //                                   ? doc.manuallyAddConsumptionData
    //                                     ? "Yes"
    //                                     : "No"
    //                                   : "NA"
    //                               }
    //                               disabled={true}
    //                               displayEmpty
    //                             >
    //                               <MenuItem value="NA">NA</MenuItem>
    //                               <MenuItem value="No">No</MenuItem>
    //                               <MenuItem value="Yes">Yes</MenuItem>
    //                             </Select>
    //                           </FormControl>
    //                         </TableCell>
    //                       </TableRow>
    //                     ))}
    //                   </TableBody>
    //                 </Table>
    //               </TableContainer>
    //             )}
    //           </form>
    //           <Divider
    //             style={{
    //               margin: "80px 00px 0px 00px",
    //               backgroundColor: "#707070",
    //               height: 1,
    //             }}
    //           />
    //         </div>
    //       </div>
    //     </>
    //   );
    // }

    return (
      <>
        <div className="cmo-stepper-wrapper plant-details-step">
          <div className="stepper-container">
            <form autoComplete="off">
              {isProductsLoading ? (
                <div className={classes.loaderContainer}>
                  <CircularProgress />
                </div>
              ) : (
                <TableContainer
                  component={Paper}
                  style={{ minWidth: 400, marginTop: 16, marginBottom: 16 }}
                >
                  <Table>
                    <TableHead style={{ backgroundColor: "#f9f9fc" }}>
                      <TableRow>
                        <TableCell style={{ fontSize: 16 }}>
                          Selection
                        </TableCell>
                        <TableCell style={{ fontSize: 16 }}>
                          Product Type
                        </TableCell>
                        <TableCell style={{ fontSize: 16 }}>
                          Consumption info req. For IBD/GRN
                        </TableCell>
                        <TableCell style={{ fontSize: 16 }}>
                          Consumption data present
                        </TableCell>
                        <TableCell style={{ fontSize: 16 }}>
                          Consumption details = Allocation details
                        </TableCell>
                        <TableCell style={{ fontSize: 16 }}>
                          Consumption data entered manually
                        </TableCell>
                        <TableCell style={{ fontSize: 16 }}>
                          Expiry Date Provided
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productTypesData.map((doc) => (
                        <TableRow key={doc.productTypeID}>
                          <TableCell>
                            <span style={{ position: "relative" }}>
                              <IOSSwitch
                                className={classes.iosSwitch}
                                checked={doc.activated === true}
                                onChange={(e) =>
                                  handleToggle(
                                    doc.productTypeID,
                                    e.target.checked,
                                  )
                                }
                                color="primary"
                                disabled={!editMode}
                              />
                              {doc.activated === true && (
                                <Check className={classes.checkedSwitch} />
                              )}
                            </span>
                          </TableCell>
                          <TableCell>{doc.productTypeName}</TableCell>
                          <TableCell>
                            <FormControl
                              variant="outlined"
                              size="small"
                              className={classes.mandatoryDropdown}
                            >
                              <Select
                                value={
                                  typeof doc.consumptionDataReqdForIBDGRN ===
                                  "boolean"
                                    ? doc.consumptionDataReqdForIBDGRN
                                      ? "Yes"
                                      : "No"
                                    : "NA"
                                }
                                onChange={(e) =>
                                  handleMandatoryChange(
                                    doc.productTypeID,
                                    "consumptionDataReqdForIBDGRN",
                                    e.target.value,
                                  )
                                }
                                disabled={!editMode}
                                displayEmpty
                              >
                                {/* <MenuItem value="NA">NA</MenuItem> */}
                                <MenuItem value="No">No</MenuItem>
                                <MenuItem value="Yes">Yes</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl
                              variant="outlined"
                              size="small"
                              className={classes.mandatoryDropdown}
                            >
                              <Select
                                value={
                                  typeof doc.uploadConsumptionData === "boolean"
                                    ? doc.uploadConsumptionData
                                      ? "Yes"
                                      : "No"
                                    : "NA"
                                }
                                onChange={(e) =>
                                  handleMandatoryChange(
                                    doc.productTypeID,
                                    "uploadConsumptionData",
                                    e.target.value,
                                  )
                                }
                                displayEmpty
                                disabled={
                                  !editMode ||
                                  (typeof doc.consumptionDataReqdForIBDGRN ===
                                  "boolean"
                                    ? doc.consumptionDataReqdForIBDGRN
                                      ? false
                                      : true
                                    : true)
                                }
                              >
                                {/* <MenuItem value="NA">NA</MenuItem> */}
                                <MenuItem value="No">No</MenuItem>
                                <MenuItem
                                  value="Yes"
                                  disabled={
                                    typeof doc.consumptionSameAsAllocation ===
                                    "boolean"
                                      ? doc.consumptionSameAsAllocation
                                        ? true
                                        : false
                                      : false
                                  }
                                >
                                  Yes
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl
                              variant="outlined"
                              size="small"
                              className={classes.mandatoryDropdown}
                            >
                              <Select
                                value={
                                  typeof doc.consumptionSameAsAllocation ===
                                  "boolean"
                                    ? doc.consumptionSameAsAllocation
                                      ? "Yes"
                                      : "No"
                                    : "NA"
                                }
                                onChange={(e) =>
                                  handleMandatoryChange(
                                    doc.productTypeID,
                                    "consumptionSameAsAllocation",
                                    e.target.value,
                                  )
                                }
                                displayEmpty
                                disabled={
                                  !editMode ||
                                  (typeof doc.consumptionDataReqdForIBDGRN ===
                                  "boolean"
                                    ? doc.consumptionDataReqdForIBDGRN
                                      ? false
                                      : true
                                    : true) ||
                                  (typeof doc.uploadConsumptionData ===
                                  "boolean"
                                    ? doc.uploadConsumptionData
                                      ? true
                                      : false
                                    : true)
                                }
                              >
                                {/* <MenuItem value="NA">NA</MenuItem> */}
                                <MenuItem value="No">No</MenuItem>
                                <MenuItem
                                  value="Yes"
                                  disabled={
                                    typeof doc.uploadConsumptionData ===
                                    "boolean"
                                      ? doc.uploadConsumptionData
                                        ? true
                                        : false
                                      : false
                                  }
                                >
                                  Yes
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl
                              variant="outlined"
                              size="small"
                              className={classes.mandatoryDropdown}
                            >
                              <Select
                                value={
                                  typeof doc.manuallyAddConsumptionData ===
                                  "boolean"
                                    ? doc.manuallyAddConsumptionData
                                      ? "Yes"
                                      : "No"
                                    : "NA"
                                }
                                onChange={(e) =>
                                  handleMandatoryChange(
                                    doc.productTypeID,
                                    "manuallyAddConsumptionData",
                                    e.target.value,
                                  )
                                }
                                displayEmpty
                                disabled={true}
                              >
                                {/* <MenuItem value="NA">NA</MenuItem> */}
                                <MenuItem value="No">No</MenuItem>
                                <MenuItem value="Yes">Yes</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl
                              variant="outlined"
                              size="small"
                              className={classes.mandatoryDropdown}
                            >
                              <Select
                                value={
                                  typeof doc.expiryDateProvided === "boolean"
                                    ? doc.expiryDateProvided
                                      ? "Yes"
                                      : "No"
                                    : "Yes"
                                }
                                onChange={(e) =>
                                  handleMandatoryChange(
                                    doc.productTypeID,
                                    "expiryDateProvided",
                                    e.target.value,
                                  )
                                }
                                disabled={!editMode}
                                displayEmpty
                              >
                                <MenuItem value="Yes">Yes</MenuItem>
                                <MenuItem value="No">No</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </form>
            <Divider
              style={{
                margin: "80px 00px 0px 00px",
                backgroundColor: "#707070",
                height: 1,
              }}
            />
          </div>
        </div>
      </>
    );
  },
);

export default ProductsTypesSection;
